import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ImportWizard } from "@/components/lists/ImportWizard";

export default function ContactLists() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [importList, setImportList] = useState(null);
  const [draft, setDraft] = useState({ name: "", description: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["contact-lists", page],
    queryFn: () => get(`/contact-lists?page=${page}&page_size=25`),
    refetchInterval: 8000,
  });

  const create = useMutation({
    mutationFn: () => api.post("/contact-lists", draft).then((r) => r.data),
    onSuccess: (list) => {
      toast.success("Contact list created");
      setCreateOpen(false);
      setDraft({ name: "", description: "" });
      queryClient.invalidateQueries({ queryKey: ["contact-lists"] });
      setImportList(list);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const archive = useMutation({
    mutationFn: (id) => api.post(`/contact-lists/${id}/archive`),
    onSuccess: () => {
      toast.success("List archived");
      queryClient.invalidateQueries({ queryKey: ["contact-lists"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const statusTone = (status) =>
    ({ completed: "completed", completed_with_errors: "paused", failed: "failed",
       cancelled: "failed", archived: "paused", draft: "paused" }[status] || "running");

  return (
    <>
      <PageHeader
        testId="contact-lists-page"
        title="Contact lists"
        subtitle="Physically imported collections of contacts — a contact can belong to many lists"
        actions={can("lists", "write") ? (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)} data-testid="create-list-button">
            New list &amp; import
          </Button>
        ) : null}
      />

      <Section testId="lists-section" title={`${data?.total ?? 0} lists`}>
        <DataTable
          testId="lists-table"
          rowTestId="list-row"
          loading={isLoading}
          columns={[
            { key: "name", label: "List", render: (r) => (
              <Link to={`/lists/${r.id}`} className="font-medium text-slate-800 hover:text-blue-600" data-testid={`list-link-${r.id}`}>
                {r.name}
              </Link>
            ) },
            { key: "source", label: "Source", render: (r) => title(r.source) },
            { key: "original_filename", label: "File", render: (r) => r.original_filename || "—" },
            { key: "created_by_name", label: "Imported by" },
            { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
            { key: "total_rows", label: "Rows", render: (r) => r.stats?.total_rows ?? 0 },
            { key: "valid", label: "Valid", render: (r) => r.stats?.valid_contacts ?? 0 },
            { key: "invalid", label: "Invalid", render: (r) => r.stats?.invalid_rows ?? 0 },
            { key: "duplicates", label: "Duplicates", render: (r) => r.stats?.duplicate_rows ?? 0 },
            { key: "existing", label: "Existing", render: (r) => r.stats?.existing_contacts ?? 0 },
            { key: "new", label: "New", render: (r) => r.stats?.new_contacts ?? 0 },
            { key: "dnc", label: "DNC", render: (r) => r.stats?.dnc_matches ?? 0 },
            { key: "contact_count", label: "Contacts", render: (r) => r.contact_count ?? 0 },
            { key: "status", label: "Import status", render: (r) => (
              <StatusBadge value={statusTone(r.status)} label={title(r.status)} testId={`list-status-${r.id}`} />
            ) },
            { key: "actions", label: "", render: (r) => (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" asChild data-testid={`list-open-${r.id}`}>
                  <Link to={`/lists/${r.id}`}>Open</Link>
                </Button>
                {can("lists", "write") ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setImportList(r)} data-testid={`list-import-${r.id}`}>Import CSV</Button>
                    {r.status !== "archived" ? (
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => archive.mutate(r.id)} data-testid={`list-archive-${r.id}`}>Archive</Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) },
          ]}
          rows={data?.items || []}
          empty={{ title: "No contact lists yet", description: "Create a list and import a CSV file to get started." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white" data-testid="create-list-dialog">
          <DialogHeader><DialogTitle>New contact list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>List name</Label>
              <Input data-testid="list-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Italy August Import" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea data-testid="list-description-input" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!draft.name.trim() || create.isPending} onClick={() => create.mutate()} data-testid="list-create-submit">
              Create list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportWizard list={importList} onClose={() => setImportList(null)} />
    </>
  );
}
